import { useCallback, useEffect, useRef, useState } from "react";
import {
  getPerformerRunResult,
  startPerformerRun,
} from "../../businessModeService";
import { maybeRedirectOnSessionAuthError } from "../../../core/services/sessionExpiry";
import { buildPerformerActionInput, resolvePerformerItemIds } from "./performerActionInput";
import {
  resolvePerformerProgressForItem,
  resolveSingleItemPollUpdate,
} from "./performerProgress";
import { performerRunErrorMessage } from "./UsageLimitBanner";

const POLL_INTERVAL_MS = 1000;
const POLL_FAST_INTERVAL_MS = 350;

/**
 * Resolve the Gmail draft id to pass on retry.
 *
 * Prefers the id from the latest completed result (so successive retries keep
 * overwriting the same draft that was most recently saved).  Falls back to the
 * seed id (the id already saved in the persisted aiResult passed in by the
 * parent) when no in-session result exists yet.
 *
 * @example
 * resolveGmailDraftIdForRetry("seed-id", { gmail_draft_id: "result-id" }); // "result-id"
 * resolveGmailDraftIdForRetry("seed-id", null);                             // "seed-id"
 */
export const resolveGmailDraftIdForRetry = (seedDraftId, latestResult) => {
  const fromResult = String(latestResult?.gmail_draft_id || "").trim();
  if (fromResult) return fromResult;
  return String(seedDraftId || "").trim() || null;
};

/**
 * Re-run the AI performer on a single action item and poll until completion.
 *
 * Returns `result` (latest performer row when a row is ready, including while
 * the run is still finishing later work), `crmSideEffect` (the CRM write
 * proposed alongside `result`, e.g. a new-lead deal on an email, or null),
 * `progress` (live step list while waiting for that row), `loading` (true
 * until the primary result is ready), and `run` (function to kick off a fresh run).
 *
 * Opening/closing a details sidebar (or switching the bound item) must not abort
 * an in-flight poll — progress/results are reported via `onStateChange(stableId, state)`
 * so parents can keep showing work on the row.
 *
 * @example
 * // Input
 * const { result, loading, run } = useSingleItemPerformerRun(item, workflowId);
 * // Usage
 * <button onClick={run} disabled={loading}>
 *   {loading ? "Working…" : "Run AI"}
 * </button>
 * // Output (after run completes)
 * // result === { outcome: "success", draft: "Hi Bob, …", … }
 * // loading === false
 *
 * @example
 * // Input: parent increments resultResetKey when a bulk performer run starts
 * useSingleItemPerformerRun(item, workflowId, bulkEpoch);
 * // Output: cached single-run result clears so rows do not show stale drafts next to fresh bulk state
 *
 * @example
 * // Input: sidebar closes mid-run (item becomes {})
 * useSingleItemPerformerRun({}, undefined, 0, "", onStateChange);
 * // Output: polling continues; onStateChange(stableId, { loading: true, … }) keeps the row alive
 */
export const useSingleItemPerformerRun = (
  item,
  workflowId,
  resultResetKey = 0,
  seedGmailDraftId = "",
  onStateChange = null
) => {
  const [result, setResult] = useState(null);
  const [crmSideEffect, setCrmSideEffect] = useState(null);
  const [progress, setProgress] = useState({ steps: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const pollRef = useRef(null);
  const pollInFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const itemRef = useRef(item);
  const workflowIdRef = useRef(workflowId);
  const onStateChangeRef = useRef(onStateChange);
  const resultOwnerStableIdRef = useRef("");
  const pollOwnerStableIdRef = useRef("");
  const pollOwnerItemRef = useRef(null);
  const pollOwnerWorkflowIdRef = useRef("");
  const latestResultByOwnerRef = useRef(new Map());
  const latestCrmSideEffectByOwnerRef = useRef(new Map());
  const pollRunOnceRef = useRef(null);
  const pollTimerMsRef = useRef(POLL_INTERVAL_MS);
  const currentStableId = resolvePerformerItemIds(item, workflowId).stableId;

  useEffect(() => {
    itemRef.current = item;
    workflowIdRef.current = workflowId;
  }, [item, workflowId]);

  useEffect(() => {
    onStateChangeRef.current = onStateChange;
  }, [onStateChange]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const emitState = useCallback((stableId, next) => {
    if (!stableId || !onStateChangeRef.current) return;
    onStateChangeRef.current(stableId, next);
  }, []);

  const clearRunState = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollInFlightRef.current = false;
    setLoading(false);
    setResult(null);
    setCrmSideEffect(null);
    setProgress({ steps: [] });
    setError("");
    resultOwnerStableIdRef.current = "";
    pollOwnerStableIdRef.current = "";
    pollOwnerItemRef.current = null;
    pollOwnerWorkflowIdRef.current = "";
  }, []);

  useEffect(() => {
    clearRunState();
  }, [resultResetKey, clearRunState]);

  // Switching the bound item (details open/close or another task) must not abort
  // an in-flight poll for a different owner — only refresh local display state.
  useEffect(() => {
    setError("");
    const pollOwner = pollOwnerStableIdRef.current;
    const resultOwner = resultOwnerStableIdRef.current;

    if (pollOwner && pollOwner === currentStableId && pollRef.current) {
      const readyResult = latestResultByOwnerRef.current.get(currentStableId) || null;
      setCrmSideEffect(latestCrmSideEffectByOwnerRef.current.get(currentStableId) || null);
      if (readyResult) {
        setResult(readyResult);
        setLoading(false);
        setProgress({ steps: [] });
      } else {
        setLoading(true);
      }
      return;
    }

    if (pollOwner && pollOwner !== currentStableId) {
      setLoading(false);
      setProgress({ steps: [] });
      if (resultOwner === currentStableId) {
        setResult(latestResultByOwnerRef.current.get(currentStableId) || null);
        setCrmSideEffect(latestCrmSideEffectByOwnerRef.current.get(currentStableId) || null);
      } else {
        setResult(null);
        setCrmSideEffect(null);
      }
      return;
    }

    if (resultOwner === currentStableId) {
      setResult(latestResultByOwnerRef.current.get(currentStableId) || null);
      setCrmSideEffect(latestCrmSideEffectByOwnerRef.current.get(currentStableId) || null);
      setLoading(false);
      setProgress({ steps: [] });
      return;
    }

    setLoading(false);
    setResult(null);
    setCrmSideEffect(null);
    setProgress({ steps: [] });
  }, [currentStableId]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollInFlightRef.current = false;
    pollTimerMsRef.current = POLL_INTERVAL_MS;
  }, []);

  const startPollInterval = useCallback((runId, intervalMs) => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollTimerMsRef.current = intervalMs;
    pollRef.current = setInterval(() => {
      void pollRunOnceRef.current?.(runId);
    }, intervalMs);
  }, []);

  const pollRunOnce = useCallback(async (runId) => {
    if (pollInFlightRef.current) return;
    const pollStableId = pollOwnerStableIdRef.current;
    if (!pollStableId) return;
    pollInFlightRef.current = true;
    try {
      const data = await getPerformerRunResult(runId);
      if (!mountedRef.current) return;
      if (pollOwnerStableIdRef.current !== pollStableId) return;
      const ownerItem = pollOwnerItemRef.current || itemRef.current;
      const ownerWorkflowId =
        pollOwnerWorkflowIdRef.current || workflowIdRef.current;
      const itemProgress = resolvePerformerProgressForItem(
        ownerItem,
        data.progress,
        ownerWorkflowId
      );
      const progressPayload = itemProgress?.steps?.length
        ? itemProgress
        : { steps: [] };
      const update = resolveSingleItemPollUpdate({
        status: data.status,
        results: data.result?.results,
        stableId: pollStableId,
        steps: progressPayload.steps,
      });
      const wantedInterval = update.pollFast ? POLL_FAST_INTERVAL_MS : POLL_INTERVAL_MS;
      if (pollRef.current && pollTimerMsRef.current !== wantedInterval) {
        startPollInterval(runId, wantedInterval);
      }

      if (update.applyResult) {
        resultOwnerStableIdRef.current = pollStableId;
        latestResultByOwnerRef.current.set(pollStableId, update.applyResult);
      }
      if (update.applyCrmSideEffect) {
        latestCrmSideEffectByOwnerRef.current.set(pollStableId, update.applyCrmSideEffect);
      }
      const nextResult =
        update.applyResult ||
        latestResultByOwnerRef.current.get(pollStableId) ||
        null;
      const nextCrmSideEffect =
        update.applyCrmSideEffect ||
        latestCrmSideEffectByOwnerRef.current.get(pollStableId) ||
        null;
      const showProgress = update.loading && progressPayload.steps.length;
      const nextProgress = showProgress ? progressPayload : { steps: [] };

      if (update.stop) {
        stopPolling();
        pollOwnerStableIdRef.current = "";
        pollOwnerItemRef.current = null;
        pollOwnerWorkflowIdRef.current = "";
      }

      emitState(pollStableId, {
        loading: update.loading,
        progress: nextProgress,
        result: nextResult,
        crmSideEffect: nextCrmSideEffect,
        error: "",
        done: update.stop,
      });
      const currentStableIdNow = resolvePerformerItemIds(
        itemRef.current,
        workflowIdRef.current
      ).stableId;
      if (pollStableId === currentStableIdNow) {
        if (nextResult) setResult(nextResult);
        // Unlike the primary draft, a CRM side-effect can legitimately stop being
        // proposed on a retry (e.g. the new draft no longer reads as a new lead),
        // so this must track truth exactly rather than only ever overwrite-on-truthy.
        setCrmSideEffect(nextCrmSideEffect);
        setLoading(update.loading);
        setProgress(nextProgress);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      const pollStableIdNow = pollOwnerStableIdRef.current;
      const keptResult = pollStableIdNow
        ? latestResultByOwnerRef.current.get(pollStableIdNow) || null
        : null;
      const keptCrmSideEffect = pollStableIdNow
        ? latestCrmSideEffectByOwnerRef.current.get(pollStableIdNow) || null
        : null;
      stopPolling();
      pollOwnerStableIdRef.current = "";
      pollOwnerItemRef.current = null;
      pollOwnerWorkflowIdRef.current = "";
      if (pollStableIdNow) {
        emitState(pollStableIdNow, {
          loading: false,
          progress: { steps: [] },
          result: keptResult,
          crmSideEffect: keptCrmSideEffect,
          error: keptResult
            ? ""
            : performerRunErrorMessage(err),
          done: true,
        });
      }
      setLoading(false);
      maybeRedirectOnSessionAuthError(err?.message);
    } finally {
      pollInFlightRef.current = false;
    }
  }, [emitState, startPollInterval, stopPolling]);

  useEffect(() => {
    pollRunOnceRef.current = pollRunOnce;
  }, [pollRunOnce]);

  const startPolling = useCallback(
    (runId, ownerStableId, ownerItem, ownerWorkflowId) => {
      stopPolling();
      pollOwnerStableIdRef.current = ownerStableId;
      pollOwnerItemRef.current = ownerItem || null;
      pollOwnerWorkflowIdRef.current = ownerWorkflowId || "";
      // A retry's fresh run may no longer propose a CRM side-effect at all — drop
      // the previous attempt's proposal so it cannot linger as stale/misleading.
      latestResultByOwnerRef.current.delete(ownerStableId);
      latestCrmSideEffectByOwnerRef.current.delete(ownerStableId);
      emitState(ownerStableId, {
        loading: true,
        progress: { steps: [] },
        result: null,
        crmSideEffect: null,
        error: "",
      });
      void pollRunOnce(runId);
      startPollInterval(runId, POLL_INTERVAL_MS);
    },
    [emitState, pollRunOnce, startPollInterval, stopPolling]
  );

  const run = useCallback(async (opts = {}) => {
    const runStableId = resolvePerformerItemIds(item, workflowId).stableId;
    if (!runStableId) return;
    const ownedResult =
      resultOwnerStableIdRef.current === runStableId
        ? latestResultByOwnerRef.current.get(runStableId) || result
        : latestResultByOwnerRef.current.get(runStableId) || null;
    resultOwnerStableIdRef.current = runStableId;
    latestResultByOwnerRef.current.delete(runStableId);
    latestCrmSideEffectByOwnerRef.current.delete(runStableId);
    setLoading(true);
    setResult(null);
    setCrmSideEffect(null);
    setProgress({ steps: [] });
    setError("");
    emitState(runStableId, {
      loading: true,
      progress: { steps: [] },
      result: null,
      crmSideEffect: null,
      error: "",
    });
    const { revisionNotes = "", previousDraft = "", previousSubject = "" } = opts || {};
    try {
      const gmailDraftId = resolveGmailDraftIdForRetry(seedGmailDraftId, ownedResult);
      const actionInput = await buildPerformerActionInput(item, workflowId, gmailDraftId, {
        revisionNotes,
        previousDraft,
        previousSubject,
      });
      const { run_id } = await startPerformerRun({ actionItems: [actionInput] });
      if (!mountedRef.current) return;
      // If the bound item changed while starting, still poll for the owner we started.
      startPolling(run_id, runStableId, item, workflowId);
      const currentStableIdNow = resolvePerformerItemIds(
        itemRef.current,
        workflowIdRef.current
      ).stableId;
      if (runStableId !== currentStableIdNow) {
        setLoading(false);
        setProgress({ steps: [] });
      }
    } catch (err) {
      if (!mountedRef.current) return;
      stopPolling();
      pollOwnerStableIdRef.current = "";
      pollOwnerItemRef.current = null;
      pollOwnerWorkflowIdRef.current = "";
      setLoading(false);
      const message = performerRunErrorMessage(err);
      setError(message);
      emitState(runStableId, {
        loading: false,
        progress: { steps: [] },
        result: null,
        crmSideEffect: null,
        error: message,
        done: true,
      });
      maybeRedirectOnSessionAuthError(err?.message);
    }
  }, [item, workflowId, seedGmailDraftId, result, emitState, startPolling, stopPolling]);

  /** Hide the current error (user pressed ×). The next run starts with a clean slate anyway. */
  const dismissError = useCallback(() => setError(""), []);

  const ownsCurrentItem =
    Boolean(currentStableId) &&
    (resultOwnerStableIdRef.current === currentStableId ||
      pollOwnerStableIdRef.current === currentStableId);
  const activePollForCurrent =
    Boolean(currentStableId) && pollOwnerStableIdRef.current === currentStableId;

  return {
    result:
      resultOwnerStableIdRef.current === currentStableId
        ? result || latestResultByOwnerRef.current.get(currentStableId) || null
        : null,
    crmSideEffect:
      resultOwnerStableIdRef.current === currentStableId
        ? crmSideEffect || latestCrmSideEffectByOwnerRef.current.get(currentStableId) || null
        : null,
    progress: activePollForCurrent && loading ? progress : { steps: [] },
    loading: Boolean(ownsCurrentItem && loading),
    error: ownsCurrentItem ? error : "",
    dismissError,
    run,
  };
};
